import React from 'react';
import { FiExternalLink } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { buildGolfNowBookingUrl } from '../services/golfNowService';

/**
 * Live tee-times status header for the Smart results pages.
 *
 *  - Live tee times loaded:  "Showing GolfNow tee times for <name>" (name links
 *    out to GolfNow) plus an "Incorrect?" prompt.
 *  - No live tee times (wrong / missing facility id): an "Expecting live tee
 *    times?" prompt that opens the paste-a-link flow.
 *
 * The override flow only engages for real golf_clubs rows (canOverride); custom
 * / free-text courses render nothing here.
 *
 * Props:
 *   courseName    - display name (golfnow_course_name ?? course_name ?? name)
 *   facilityId    - effective GolfNow facility id (override ?? club), may be null
 *   region        - region code for the GolfNow booking URL
 *   bookingDate   - YYYY-MM-DD for the GolfNow deep link
 *   hasResults    - whether live tee times are currently displayed
 *   canOverride   - true for real clubs (enables the paste-link prompt)
 *   onOpenModal   - (mode: 'incorrect' | 'expecting') => void
 */
export default function LiveTeeTimesStatus({
  courseName,
  facilityId,
  region = 'US',
  bookingDate,
  hasResults,
  canOverride,
  onOpenModal,
}) {
  const name = courseName || 'this course';

  if (hasResults) {
    const bookingUrl = facilityId
      ? buildGolfNowBookingUrl(facilityId, bookingDate || new Date().toISOString().slice(0, 10), region)
      : null;

    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3 text-sm">
        <span className="text-text-secondary">Showing GolfNow tee times for</span>
        {bookingUrl ? (
          <a
            href={bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-semibold text-accent hover:text-interactive-hover"
          >
            {name}
            <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
          </a>
        ) : (
          <span className="font-semibold text-text-primary">{name}</span>
        )}
        {canOverride && (
          <button
            type="button"
            onClick={() => onOpenModal?.('incorrect')}
            className="text-text-muted hover:text-accent underline underline-offset-2 transition-colors"
          >
            Incorrect?
          </button>
        )}
      </div>
    );
  }

  if (!canOverride) return null;

  return (
    <div className="rounded-lg border border-border-subtle bg-surface p-4 mb-4">
      <p className="text-sm font-semibold text-text-primary">Expecting live tee times?</p>
      <p className="text-sm text-text-secondary mt-1">
        We don't have GolfNow tee times for {name} yet. If you can find it on GolfNow, paste the
        link and we'll use it for your results.
      </p>
      <button
        type="button"
        onClick={() => onOpenModal?.('expecting')}
        className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:text-interactive-hover transition-colors"
      >
        Add a GolfNow link
      </button>
    </div>
  );
}
